// src/components/individualcomp/SubmissionModal.jsx

import React, { useState, useRef, useEffect } from 'react';
import { useAuth } from "@/contexts/AuthContext";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
  DialogClose
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogFooter,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogCancel,
  AlertDialogAction,
} from '@/components/ui/alert-dialog';
import MediaSlider from '@/components/ui/MediaSlider';
import CommentSection from '@/components/ui/CommentSection';
import FireworksVoteButton from '@/components/ui/FireworksVoteButton';
import ZeloScore from '@/components/ui/ZeloScore';
import {
  deleteSubmission,
  fetchVotes,
  addVote,
  recordSubmissionView,
  recordSubmissionShare,
} from '@/services/apiService';
import { X, Link2, Check } from "lucide-react";
import SubmissionDropdown from '@/components/ui/SubmissionDropdown.jsx';
import DownloadDropdown from '@/components/ui/DownloadDropdown.jsx';
import { useModal } from "@/contexts/ModalContext";

// Dwell time (ms) before a view is recorded — prevents rapid arrow-clicks
// through submissions from inflating the view count.
const VIEW_DWELL_MS = 2000;

const SubmissionModal = () => {
  const { modalType, modalProps, closeModal } = useModal();
  if (modalType !== 'SUBMISSION_PREVIEW') return null;

  const { submission, user, onDelete, onEdit, onVoteUpdate } = modalProps;
  const { user: authUser } = useAuth();

  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // 🗳️ Vote state
  const [voteCount, setVoteCount] = useState(submission?.votes_count ?? 0);
  const [hasVoted, setHasVoted] = useState(submission?.is_liked_by_me ?? false);
  const [votesLoading, setVotesLoading] = useState(true);
  const [isVoting, setIsVoting] = useState(false);

  // 🔗 Share state (community_score only)
  const [shareCopied, setShareCopied] = useState(false);

  // ── Active slide tracking for DownloadDropdown ───────────────
  const [activeFile, setActiveFile] = useState(submission?.files?.[0] ?? null);

  const competitionId = typeof submission?.competition === 'object' && submission?.competition?.id
    ? submission.competition.id
    : submission?.competition;

  const competitionType = submission?.competition_detail?.competition_type;
  const isCommunityScore = competitionType === 'community_score';

  const isLive = submission?.competition_detail?.competition_state === 'live';

  const normalizedUser = typeof user === "object" && user !== null ? user : (user ? { id: user } : null);

  const submissionUser = submission?.user;
  const displayUser =
    typeof submissionUser === "object" && submissionUser !== null
      ? submissionUser
      : (typeof normalizedUser === "object" && normalizedUser !== null ? normalizedUser : null);

  const submissionOwnerId =
    typeof submissionUser === "object" && submissionUser !== null
      ? submissionUser.id
      : submissionUser;

  const modalContentRef = useRef(null);

  useEffect(() => {
    return () => {
      if (modalContentRef.current) {
        modalContentRef.current.querySelectorAll("video").forEach((video) => {
          video.pause();
          video.removeAttribute("src");
          video.load();
        });
      }
    };
  }, []);

  // 👁️ Record a view after a 2-second dwell — community_score only.
  // The timer is cleared if the modal closes before 2s elapses, so
  // rapid browsing doesn't register shallow views. Backend dedupes,
  // so re-opening the same submission is harmless.
  useEffect(() => {
    if (!isCommunityScore || !submission?.id) return;

    const timer = setTimeout(() => {
      recordSubmissionView(submission.id);
    }, VIEW_DWELL_MS);

    return () => clearTimeout(timer);
  }, [isCommunityScore, submission?.id]);

  // 🗳️ Load vote status on mount
  useEffect(() => {
    if (!submission?.id) {
      setVotesLoading(false);
      return;
    }

    const loadVotes = async () => {
      try {
        setVotesLoading(true);
        const response = await fetchVotes(submission.id);
        setVoteCount(response.count ?? 0);
        setHasVoted(response.user_voted ?? false);
      } catch (err) {
        console.error('Failed to load votes:', err);
      } finally {
        setVotesLoading(false);
      }
    };

    loadVotes();
  }, [submission?.id]);

  const handleVoteToggle = async (e) => {
    if (e && typeof e.stopPropagation === 'function') {
      e.stopPropagation();
    }

    if (!submission?.id || !competitionId || isVoting) return;

    setIsVoting(true);
    const previousVoteCount = voteCount;
    const previousHasVoted = hasVoted;

    try {
      const newHasVoted = !hasVoted;
      const newVoteCount = newHasVoted ? voteCount + 1 : Math.max(0, voteCount - 1);
      setHasVoted(newHasVoted);
      setVoteCount(newVoteCount);

      const payload = { comp_id: competitionId, submission_id: submission.id };
      const res = await addVote(payload);

      if (res && typeof res.count === 'number') setVoteCount(res.count);
      if (res && typeof res.user_voted === 'boolean') setHasVoted(res.user_voted);

      if (onVoteUpdate && typeof res.count === 'number') {
        onVoteUpdate(submission.id, res.user_voted ?? newHasVoted, res.count);
      }
    } catch (err) {
      console.error('Vote submission failed:', err);
      setVoteCount(previousVoteCount);
      setHasVoted(previousHasVoted);
    } finally {
      setIsVoting(false);
    }
  };

  // 🔗 Copy submission deep-link + record share (community_score only)
  const handleShare = async () => {
    const url = `${window.location.origin}${window.location.pathname}?submission=${submission.id}`;
    try {
      await navigator.clipboard.writeText(url);
      setShareCopied(true);
      setTimeout(() => setShareCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy share link:', err);
    }
    // Record the share regardless of clipboard success — the intent was there
    recordSubmissionShare(submission.id);
  };

  const getUserDisplayName = (userObj) => {
    if (!userObj || typeof userObj !== "object") return 'Anonymous';
    if (userObj.profile?.name?.trim()) return userObj.profile.name.trim();
    const firstName = userObj.first_name || '';
    const lastName = userObj.last_name || '';
    const fullName = [firstName, lastName].filter(Boolean).join(' ').trim();
    return fullName || userObj.username || 'Anonymous';
  };

  const handleDeleteSubmission = async () => {
    try {
      await deleteSubmission(submission.id);
      onDelete(submission.id);
      setShowDeleteConfirm(false);
      closeModal();
    } catch (err) {
      console.error('Failed to delete submission', err);
    }
  };

  const isOwner = authUser?.id && Number(submissionOwnerId) === Number(authUser.id);

  return (
    <>
      <Dialog open onOpenChange={(open) => { if (!open) closeModal(); }}>
        <DialogContent
          className="max-w-4xl h-[90vh] bg-stone-brown flex flex-col"
          ref={modalContentRef}
        >
          <div className="sr-only">
            <DialogTitle>Submission</DialogTitle>
            <DialogDescription>Details and interactions</DialogDescription>
          </div>

          <div className="flex-1 overflow-y-auto scrollbar-hide">
            {/* Slider — notifies us when active slide changes */}
            <MediaSlider
              files={submission.files}
              onActiveFileChange={setActiveFile}
            />

            <div className="px-6 space-y-2 mt-4">
              <div className="flex items-start justify-between">
                <div className="flex items-start gap-4 flex-1">
                  {/* Avatar */}
                  <div className="flex flex-col gap-4">
                    {displayUser?.profile?.avatar_url ? (
                      <img
                        src={displayUser.profile.avatar_url}
                        alt={getUserDisplayName(displayUser)}
                        className="w-14 h-14 rounded-full object-cover border border-field-grey"
                      />
                    ) : (
                      <div className="w-14 h-14 rounded-full bg-gradient-to-br from-mikado-yellow to-orange-500 flex items-center justify-center text-sm font-bold text-black">
                        {(getUserDisplayName(displayUser) || 'A')
                          .split(' ')
                          .map((n) => n[0])
                          .join('')
                          .toUpperCase()
                          .slice(0, 2)}
                      </div>
                    )}
                  </div>

                  {/* Meta */}
                  <div className="flex-1 min-w-0">
                    <p className="text-mikado-yellow font-semibold text-sm mb-2">
                      {getUserDisplayName(displayUser)}
                    </p>
                    <h2 className="font-poppins font-semibold text-lg text-swan mb-3 leading-relaxed">
                      {submission.description}
                    </h2>
                    <p className="text-xs text-field-grey">
                      {new Date(submission.submission_date).toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric',
                      })}
                    </p>
                  </div>
                </div>

                {/* ── Action buttons: Download (everyone) + Edit/Delete (owner only) ── */}
                <div className="flex items-center gap-1">
                  <DownloadDropdown
                    submissionId={submission.id}
                    activeFile={activeFile}
                    totalFiles={submission?.files?.length ?? 1}
                  />
                  {isOwner && (
                    <SubmissionDropdown
                      onEdit={() => onEdit(submission)}
                      onDelete={() => setShowDeleteConfirm(true)}
                    />
                  )}
                </div>
              </div>

              {/* ─────────────────────────────────────────────────────────
                  Interaction row.

                  Community Score:
                    [Vote]  [Share]  ............................  [ZeloScore]
                    Actions on the left, the live score on the right.
                    The proximity implies the actions drive the number,
                    without instructing the user (mystery is the hook).

                  Voting:
                    [Vote]   — unchanged from before.
                 ───────────────────────────────────────────────────────── */}
              <div className="py-4 border-b border-field-grey flex items-center justify-between">
                <div className="flex items-center gap-4">
                  {!votesLoading && (
                    <FireworksVoteButton
                      isLiked={hasVoted}
                      votes={voteCount}
                      onLike={isLive ? handleVoteToggle : null}
                      disabled={!isLive}
                    />
                  )}

                  {/* Submission-level share — community_score only */}
                  {isCommunityScore && (
                    <button
                      onClick={handleShare}
                      className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full
                                 border border-field-grey/40 text-swan text-sm font-poppins
                                 hover:border-mikado-yellow/50 hover:text-mikado-yellow
                                 transition-colors duration-200 cursor-pointer"
                      title="Copy link to this submission"
                    >
                      {shareCopied ? (
                        <>
                          <Check className="w-4 h-4" />
                          Copied
                        </>
                      ) : (
                        <>
                          <Link2 className="w-4 h-4" />
                          Share
                        </>
                      )}
                    </button>
                  )}
                </div>

                {/* Live ZeloScore — community_score only, right-aligned */}
                {isCommunityScore && (
                  <ZeloScore score={submission.zelo_score} size="modal" />
                )}
              </div>

              <div className="py-4">
                <CommentSection
                  submissionId={submission.id}
                  currentUser={authUser}
                  allowDelete={true}
                  allowInput={true}
                />
              </div>
            </div>
          </div>
        </DialogContent>

        <DialogClose
          className="fixed top-6 right-6 z-[9999] cursor-pointer pointer-events-auto text-swan hover:text-field-grey transition-colors"
          onClick={closeModal}
        >
          <span className="sr-only">Close</span>
          <X className="w-6 h-6" />
        </DialogClose>
      </Dialog>

      <AlertDialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <AlertDialogContent className="bg-stone-brown">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-swan">
              Are you sure you wish to delete your submission?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-field-grey">
              This action is irreversible.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteSubmission}>
              Confirm
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default SubmissionModal;